import { useEffect, useState, useRef } from "react";
import { FiRefreshCw } from "react-icons/fi";
import { Calculator } from "lucide-react";
import { showError, showSuccess } from "../../../utils/toastUtils";
import api from "../../../configs/api";
import DownloadPdf from "../../../components/User/Common/DownloadPdf";

const CalculatorTab = () => {
  const [plants, setPlants] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [allMixtures, setAllMixtures] = useState([]);
  const [usedMixturesByPlant, setUsedMixturesByPlant] = useState({}); // { plantId: Set<mixtureId> }
  const [loading, setLoading] = useState(true);

  const [selectedPlant, setSelectedPlant] = useState("");
  const [selectedMixture, setSelectedMixture] = useState("");

  // { [productId]: { quantity: string, price: string } }
  const [productInputs, setProductInputs] = useState({});

  const [calculating, setCalculating] = useState(false);
  const [results, setResults] = useState(null);

  const pdfRef = useRef();

  useEffect(() => {
    if (!selectedPlant || !selectedMixture) return;

    const mappedInputs = {};

    allProducts.forEach((product) => {
      const gradeData = product.gradeMixtures?.find(
        (g) => String(g.grade?._id || g.grade) === String(selectedMixture),
      );

      const quantity = gradeData?.quantity || "";

      const defaultVariety = product.variety?.[0];

      mappedInputs[product._id] = {
        quantity,
        price: defaultVariety?.price || "",
        varietyId: defaultVariety?._id || "",
      };
    });

    setProductInputs(mappedInputs);
  }, [selectedPlant, selectedMixture, allProducts]);

  useEffect(() => {
    if (!selectedPlant || !selectedMixture) return;

    const products = allProducts
      .map((p) => {
        const input = productInputs[p._id] || {};

        return {
          productId: p._id,
          varietyId: input.varietyId,
          quantity: Number(input.quantity || 0),
          pricePerUnit: Number(input.price || 0),
        };
      })
      .filter((p) => p.quantity > 0);

    if (!products.length) return;

    const calculate = async () => {
      try {
        setCalculating(true);

        const res = await api.post("/calculator/calculate", {
          plantId: selectedPlant,
          mixtureId: selectedMixture,
          products,
        });

        setResults(res.data.data);
      } catch (err) {
        console.error(err);
      } finally {
        setCalculating(false);
      }
    };

    calculate();
  }, [productInputs]);

  useEffect(() => {
    const fetchInit = async () => {
      try {
        setLoading(true);

        const [pRes, prRes, mRes, histRes] = await Promise.all([
          api.get("/calculator/plants"),
          api.get("/calculator/products"),
          api.get("/grade-mixtures"),
          api.get("/calculator/history"),
        ]);

        const plantsData = pRes.data.data;
        const productsData = prRes.data.data;
        const mixturesData = mRes.data.data;

        setPlants(plantsData);
        setAllProducts(productsData);
        setAllMixtures(mixturesData);

        const usedMap = {};
        (histRes.data.data || []).forEach((calc) => {
          const plantId = calc.plant?._id || calc.plant;
          const mixtureId = calc.gradeMixture?._id || calc.gradeMixture;

          if (plantId && mixtureId) {
            if (!usedMap[plantId]) usedMap[plantId] = new Set();
            usedMap[plantId].add(String(mixtureId));
          }
        });

        setUsedMixturesByPlant(usedMap);

        if (plantsData.length > 0) {
          const firstPlant = plantsData[0];
          setSelectedPlant(firstPlant._id);

          const plantMixtureIds =
            firstPlant.gradeMixtures?.map((m) => String(m._id ?? m)) || [];

          const firstMixture = mixturesData.find((m) =>
            plantMixtureIds.includes(String(m._id)),
          );

          if (firstMixture) {
            setSelectedMixture(firstMixture._id);
          }
        }
      } catch {
        showError("Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    fetchInit();
  }, []);

  // Mixtures linked to the selected plant (from plant.gradeMixtures)
  const plantMixtureIds = (() => {
    if (!selectedPlant) return null;
    const plant = plants.find((p) => String(p._id) === selectedPlant);
    if (!plant?.gradeMixtures?.length) return null;
    return new Set(plant.gradeMixtures.map((m) => String(m._id ?? m)));
  })();

  // Available mixtures: belongs to plant AND not already used for this plant
  const availableMixtures = allMixtures.filter((m) => {
    const id = String(m._id);

    // Only filter by plant linkage (optional)
    if (plantMixtureIds && !plantMixtureIds.has(id)) return false;

    return true;
  });

  const setField = (productId, field, value) => {
    setProductInputs((prev) => ({
      ...prev,
      [productId]: { ...prev[productId], [field]: value },
    }));
    setResults(null);
  };

  const getInput = (productId) =>
    productInputs[productId] ?? { quantity: "", price: "" };

  const getProductCost = (productId) => {
    const { quantity, price } = getInput(productId);
    const qty = Number(quantity);
    const prc = Number(price);
    if (!qty || qty <= 0 || !prc || prc <= 0) return null;
    return qty * prc;
  };

  const totalPreviewCost = allProducts.reduce((sum, p) => {
    const cost = getProductCost(p._id);
    return sum + (cost ?? 0);
  }, 0);

  const handleCalculate = async () => {
    if (!selectedPlant || !selectedMixture)
      return showError("Select plant and grade mixture");

    const products = allProducts
      .map((p) => {
        const { quantity, price } = getInput(p._id);
        return {
          productId: p._id,
          quantity: Number(quantity ?? 0),
          pricePerUnit: Number(price ?? 0),
        };
      })
      .filter((p) => p.quantity > 0);

    if (!products.length)
      return showError("Enter quantity for at least one product");

    const missingPrice = products.find(
      (p) => !p.pricePerUnit || p.pricePerUnit <= 0,
    );
    if (missingPrice) {
      const name = allProducts.find(
        (p) => p._id === missingPrice.productId,
      )?.name;
      return showError(`Enter price for "${name}"`);
    }

    try {
      setCalculating(true);
      setResults(null);
      const res = await api.post("/calculator/calculate", {
        plantId: selectedPlant,
        mixtureId: selectedMixture,
        products,
      });
      const savedData = res.data.data;
      setResults(savedData);

      // Mark this mixture as used for this plant so it disappears from the dropdown
      setUsedMixturesByPlant((prev) => {
        const next = { ...prev };
        if (!next[selectedPlant]) next[selectedPlant] = new Set();
        else next[selectedPlant] = new Set(next[selectedPlant]); // clone
        next[selectedPlant].add(String(selectedMixture));
        return next;
      });

      showSuccess("Calculation saved");
    } catch (err) {
      showError(err?.response?.data?.message || "Calculation failed");
    } finally {
      setCalculating(false);
    }
  };

  const handleReset = () => {
    setSelectedPlant("");
    setSelectedMixture("");
    setProductInputs({});
    setResults(null);
  };

  const canCalculate =
    selectedPlant &&
    selectedMixture &&
    allProducts.some((p) => Number(getInput(p._id).quantity) > 0);

  const showProductTable = true;

  return (
    <div className="max-w-7xl mx-auto space-y-5">
      {/* ── Step 1: Plant & Grade Mixture ── */}
      <div className="border border-borderLight bg-surfaceColor p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-8 h-8 flex items-center justify-center bg-primaryAccent/10 text-primaryAccent">
            <Calculator size={18} />
          </div>
          <p className="text-sm font-semibold text-headingText">
            Configure Calculation
          </p>
        </div>

        <div className="border border-borderLight bg-surfaceColor p-6 space-y-6 w-full">
          {/* PLANTS */}
          <div className="w-full">
            <p className="text-xs text-descriptionText mb-2">Select Plant</p>

            <div className="grid grid-cols-[repeat(auto-fit,minmax(120px,1fr))] gap-3 w-full">
              {plants.map((p) => (
                <div
                  key={p._id}
                  onClick={() => {
                    setSelectedPlant(p._id);
                    setSelectedMixture("");
                    setProductInputs({});
                    setResults(null);
                  }}
                  className={`p-3 border cursor-pointer text-sm text-center
            ${
              selectedPlant === p._id
                ? "bg-primaryAccent text-white border-primaryAccent"
                : "border-borderLight hover:border-primaryAccent"
            }
          `}
                >
                  {p.plantName}
                </div>
              ))}
            </div>
          </div>

          {/* GRADES */}
          <div className="w-full">
            <p className="text-xs text-descriptionText mb-2">Select Grade</p>

            <div className="grid grid-cols-[repeat(auto-fit,minmax(120px,1fr))] gap-3 w-full">
              {availableMixtures.map((m) => (
                <div
                  key={m._id}
                  onClick={() => setSelectedMixture(m._id)}
                  className={`p-3 border cursor-pointer text-sm text-center
            ${
              selectedMixture === m._id
                ? "bg-primaryAccent text-white border-primaryAccent"
                : "border-borderLight hover:border-primaryAccent"
            }
          `}
                >
                  {m.gradeName}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Step 2: Products Table ── */}
      {showProductTable && (
        <div className="border border-borderLight bg-surfaceColor p-6">
          <div className="flex justify-between mb-5">
            <p className="text-sm font-semibold text-headingText mb-4">
              Products
            </p>
            <button
              onClick={() => {
                pdfRef.current?.download();
              }}
              className="px-4 py-2 bg-blue-400 text-white text-sm disabled:opacity-50"
            >
              Download PDF
            </button>
          </div>

          {/* Desktop header */}
          <div className="hidden sm:grid grid-cols-[1fr_140px_140px_140px_130px] gap-3 text-[11px] font-semibold text-descriptionText uppercase tracking-wide mb-2 px-1 ">
            <span>Product</span>
            <span>Variety</span>
            <span>Price / Unit (₹) *</span>
            <span>Quantity</span>
            <span className="text-right">Cost Preview</span>
          </div>

          <div className="divide-y divide-borderLight">
            {allProducts.map((product) => {
              const { quantity, price } = getInput(product._id);
              const cost = getProductCost(product._id);
              const hasQty = Number(quantity) > 0;

              return (
                <div
                  key={product._id}
                  className="grid grid-cols-1 sm:grid-cols-[1fr_160px_140px_140px_130px] gap-3 items-center py-3"
                >
                  {/* Product name */}
                  <div>
                    <p className="text-sm font-medium text-headingText">
                      {product.name}
                    </p>

                    <p className="text-[11px] text-descriptionText">
                      {product.unit || "kg"}
                    </p>
                  </div>
                  {/* Variety Dropdown */}
                  <div>
                    <select
                      value={getInput(product._id).varietyId || ""}
                      onChange={(e) => {
                        const selectedVariety = product.variety?.find(
                          (v) => v._id === e.target.value,
                        );

                        setProductInputs((prev) => ({
                          ...prev,
                          [product._id]: {
                            ...prev[product._id],
                            varietyId: e.target.value,
                            price: selectedVariety?.price || "",
                          },
                        }));

                        setResults(null);
                      }}
                      className="w-full border border-borderLight px-2 py-1 text-sm mt-1"
                    >
                      <option value="">Select variety</option>
                      {product.variety?.map((v) => (
                        <option key={v._id} value={v._id}>
                          {v.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Price input */}
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[11px] text-descriptionText">
                      ₹
                    </span>
                    <input
                      type="number"
                      min={0}
                      placeholder="0.00"
                      value={price}
                      onChange={(e) =>
                        setField(product._id, "price", e.target.value)
                      }
                      className={`w-full border px-3 py-2 pl-6 outline-none text-sm pr-9 ${
                        hasQty && (!price || Number(price) <= 0)
                          ? "border-red-400 focus:border-red-400"
                          : "border-borderLight focus:border-primaryAccent"
                      }`}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-descriptionText">
                      /{product.unit || "kg"}
                    </span>
                  </div>

                  {/* Quantity input */}
                  <div className="relative">
                    <input
                      type="number"
                      min={0}
                      placeholder="0"
                      value={quantity}
                      onChange={(e) =>
                        setField(product._id, "quantity", e.target.value)
                      }
                      className="w-full border border-borderLight px-3 py-2 outline-none focus:border-primaryAccent text-sm pr-9"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-descriptionText">
                      {product.unit || "kg"}
                    </span>
                  </div>

                  {/* Cost preview */}
                  <div className="text-sm text-right">
                    {cost !== null ? (
                      <span className="text-primaryAccent font-medium">
                        ₹{" "}
                        {cost.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    ) : (
                      <span className="text-descriptionText italic">—</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Live Total */}
          {totalPreviewCost > 0 && (
            <div className="mt-4 pt-4 border-t border-borderLight flex justify-end">
              <div className="text-right">
                <p className="text-xs text-descriptionText mb-0.5">
                  Total Estimated Cost
                </p>
                <p className="text-xl font-bold text-primaryAccent">
                  ₹{" "}
                  {totalPreviewCost.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Empty state ── */}
      {!showProductTable && (
        <div className="border border-dashed border-borderLight p-16 text-center">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primaryAccent/10 text-primaryAccent mb-4 mx-auto">
            <Calculator size={28} />
          </div>
          <p className="text-lg font-semibold text-headingText">
            Select plant & grade mixture
          </p>
          <p className="text-sm text-descriptionText mt-1">
            Products will appear here once both are selected.
          </p>
        </div>
      )}

      {/* ── Actions ── */}
      {showProductTable && (
        <div className="flex items-center justify-between">
          <button
            onClick={handleReset}
            className="flex items-center gap-1.5 text-xs text-descriptionText hover:text-headingText transition"
          >
            <FiRefreshCw size={12} /> Reset
          </button>

          <button
            onClick={handleCalculate}
            disabled={!canCalculate || calculating}
            className="flex items-center gap-2 px-6 py-2.5 bg-primaryAccent text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition"
          >
            {calculating && (
              <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            )}
            {calculating ? "Calculating..." : "Save Calculation"}
          </button>
        </div>
      )}

      {/* ── Results ── */}
      {results && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="border border-borderLight bg-surfaceColor p-4 text-center">
              <p className="text-xs text-descriptionText mb-1">Plant</p>
              <p className="text-sm font-semibold text-headingText">
                {results.plant.plantName}
              </p>
            </div>
            <div className="border border-borderLight bg-surfaceColor p-4 text-center">
              <p className="text-xs text-descriptionText mb-1">Grade</p>
              <p className="text-sm font-semibold text-headingText">
                {results.mixture.gradeName}
              </p>
            </div>
          </div>

          <div className="border border-borderLight overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surfaceColor border-b border-borderLight">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-descriptionText uppercase tracking-wide">
                    Product
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-descriptionText uppercase tracking-wide">
                    Variety
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-descriptionText uppercase tracking-wide">
                    Quantity
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-descriptionText uppercase tracking-wide">
                    Price / Unit
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-descriptionText uppercase tracking-wide">
                    Total Cost
                  </th>
                </tr>
              </thead>
              <tbody>
                {results.rows.map((row, idx) => (
                  <tr
                    key={idx}
                    className="border-b border-borderLight hover:bg-surfaceColor transition"
                  >
                    <td className="px-4 py-3 font-medium text-headingText">
                      {row?.name}
                    </td>
                    <td className="px-4 py-3 text-descriptionText">
                      {row.varietyName || "—"}
                    </td>
                    <td className="px-4 py-3 text-right text-descriptionText">
                      {row?.quantity} {row?.unit}
                    </td>
                    <td className="px-4 py-3 text-right text-descriptionText">
                      ₹ {row.pricePerUnit.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-headingText">
                      ₹{" "}
                      {row.cost.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-surfaceColor">
                  <td
                    colSpan={3}
                    className="px-4 py-3 text-sm font-semibold text-headingText text-right"
                  >
                    Total Cost
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-base font-bold text-primaryAccent">
                      ₹{" "}
                      {results.totalCost.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
      <DownloadPdf ref={pdfRef} results={results} />
    </div>
  );
};

export default CalculatorTab;
